import { v4 as uuidv4 } from 'uuid'
import { db } from '@core/db'
import type { SectionContent } from '@/types'
import { pickCanonicalSectionContent } from '@/shared/utils/sectionContentCanonical'

export interface RemoteResumeSectionContentRow {
  id: string
  section_id: string
  version_id: string
  created_at?: string | null
  updated_at?: string | null
  deleted_at?: string | null
}

export interface ResumeSectionContentPayload {
  content: string
  contentHash?: string
  kid?: string
}

function toTimestamp(value: string | Date | null | undefined): number {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export async function upsertRemoteResumeSectionContent(
  row: RemoteResumeSectionContentRow,
  payload: ResumeSectionContentPayload,
): Promise<boolean> {
  const sectionId = row.section_id
  const versionId = row.version_id
  const existing = await db.sectionContents
    .where('[sectionId+versionId]')
    .equals([sectionId, versionId])
    .toArray() as SectionContent[]

  if (existing.some((entry) => entry.dirty)) return false

  const canonical = pickCanonicalSectionContent(existing)
  const remoteUpdatedAt = row.updated_at ?? row.created_at ?? new Date().toISOString()
  const localWatermark = canonical?.baseRemoteUpdatedAt ?? canonical?.updatedAt
  const remoteTs = toTimestamp(remoteUpdatedAt)
  const localTs = toTimestamp(localWatermark)

  if (
    canonical
    && remoteTs > 0
    && localTs > 0
    && remoteTs <= localTs
    && canonical.content === payload.content
  ) {
    return false
  }

  const targetId = canonical?.id ?? uuidv4()
  await db.sectionContents.put({
    id: targetId,
    sectionId,
    versionId,
    content: payload.content,
    createdAt: canonical?.createdAt ?? (row.created_at ? new Date(row.created_at) : new Date()),
    updatedAt: remoteUpdatedAt,
    contentHash: payload.contentHash,
    dirty: false,
    baseRemoteHash: payload.contentHash,
    baseRemoteUpdatedAt: remoteUpdatedAt,
    kid: payload.kid,
    deletedAt: row.deleted_at ?? undefined,
  } as SectionContent)

  const staleIds = existing
    .filter((entry) => entry.id !== targetId && !entry.dirty)
    .map((entry) => entry.id)
  if (staleIds.length > 0) {
    await db.sectionContents.bulkDelete(staleIds)
  }

  return true
}

export async function decodeRemoteResumeSectionContent(
  projectId: string,
  row: RemoteResumeSectionContentRow & {
    content_text?: string | null
    content_cipher?: string | null
    content_hash?: string | null
    is_encrypted?: boolean | null
  },
): Promise<ResumeSectionContentPayload | null> {
  const isEncrypted = !!(row.is_encrypted && row.content_cipher)
  if (!isEncrypted) {
    const content = row.content_text ?? ''
    return {
      content,
      contentHash: row.content_hash ?? undefined,
    }
  }

  try {
    const { getProjectSecret } = await import('@core/secrets')
    const {
      decryptText,
      hashText,
      importProjectKeyFromB64,
      keyFingerprintFromKeyB64,
    } = await import('@shared/utils/crypto')
    const secret = await getProjectSecret(projectId)
    const candidates = secret ? [secret.keyB64, ...(secret.keyHistoryB64 || [])] : []
    if (!candidates.length) return null

    const envelope = JSON.parse(row.content_cipher!) as { kid: string; iv: string; ct: string; alg: 'AES-GCM' }
    for (const b64 of candidates) {
      const fingerprint = await keyFingerprintFromKeyB64(b64)
      if (fingerprint !== envelope.kid) continue
      const key = await importProjectKeyFromB64(b64)
      const content = await decryptText(key, envelope.ct, envelope.iv)
      return {
        content,
        contentHash: row.content_hash ?? await hashText(content),
        kid: envelope.kid,
      }
    }
  } catch {
    return null
  }

  return null
}
